import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import {Box, Typography} from '@mui/material';

import {DepartmentHealthBenchmarkDeltaChip} from './DepartmentHealthBenchmarkDeltaChip';
import {DepartmentHealthBenchmarkTableSkeleton} from './DepartmentHealthBenchmarkTableSkeleton';
import {
  departmentHealthSectionCardSx,
  departmentHealthSectionSubtitleSx,
  departmentHealthSectionTitleSx,
} from './departmentHealthSectionStyles';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {getScoreStatColors} from '../../../../../components/ScoreStatCard/scoreStatUtils';
import {
  DEPARTMENT_HEALTH_DIMENSIONS,
  type DepartmentHealthBenchmarks,
  type DepartmentHealthDimension,
  type DepartmentHealthScores,
} from '../../types/DepartmentHealthTypes';

export interface Props {
  departmentName?: string;
  scores?: DepartmentHealthScores;
  benchmarks?: DepartmentHealthBenchmarks;
  loading?: boolean;
}

const headerCellSx = {
  fontSize: '10.5px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'text.secondary',
  pb: 1.125,
  px: 1,
} as const;

const bodyCellSx = {
  py: 1.125,
  px: 1,
  fontSize: '13px',
  borderBottom: '0.5px solid',
  borderColor: 'divider',
} as const;

const formatScore = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${value}%`;
};

export const DepartmentHealthBenchmarkTable = ({
  departmentName = '',
  scores,
  benchmarks,
  loading = false,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.benchmarkTable'});

  const rows = useMemo(() => {
    if (!scores || !benchmarks) {
      return null;
    }

    const {mixedRoles, roleNorm, companyAverage, deltas} = benchmarks;

    return DEPARTMENT_HEALTH_DIMENSIONS.map((dimension) => ({
      dimension,
      departmentScore: scores.departmentAverage?.[dimension] ?? null,
      roleNorm: mixedRoles ? null : (roleNorm?.[dimension] ?? null),
      companyAverage: companyAverage[dimension] ?? null,
      delta: deltas?.[dimension] ?? null,
    }));
  }, [scores, benchmarks]);

  if (loading || !rows) {
    return <DepartmentHealthBenchmarkTableSkeleton />;
  }

  return (
    <EmpionPaper sx={departmentHealthSectionCardSx} data-testid='department-health-benchmark-table'>
      <Box component='h2' sx={departmentHealthSectionTitleSx}>
        <Box component='span' sx={{color: 'text.secondary', display: 'flex', alignItems: 'center'}}>
          <CompareArrowsOutlinedIcon sx={{fontSize: 15}} />
        </Box>
        {t('title')}
      </Box>

      <Typography variant='body2' sx={{...departmentHealthSectionSubtitleSx, mb: 1.75}}>
        {t('subtitle', {departmentName})}
      </Typography>

      <Box component='table' sx={{width: '100%', borderCollapse: 'collapse'}}>
        <Box component='thead'>
          <Box component='tr' sx={{borderBottom: '0.5px solid', borderColor: 'divider'}}>
            <Box component='th' sx={{...headerCellSx, textAlign: 'left', pl: 0}}>
              {t('headers.dimension')}
            </Box>
            <Box component='th' sx={{...headerCellSx, textAlign: 'center'}}>
              {departmentName || t('headers.department')}
            </Box>
            <Box component='th' sx={{...headerCellSx, textAlign: 'center'}}>
              {t('headers.roleNorm')}
            </Box>
            <Box component='th' sx={{...headerCellSx, textAlign: 'center'}}>
              {t('headers.companyAverage')}
            </Box>
            <Box component='th' sx={{...headerCellSx, textAlign: 'left', pr: 0}}>
              {t('headers.vsNorm')}
            </Box>
          </Box>
        </Box>

        <Box component='tbody'>
          {rows.map((row) => {
            const scoreColors =
              row.departmentScore !== null ? getScoreStatColors(row.departmentScore) : null;

            return (
              <Box component='tr' key={row.dimension}>
                <Box
                  component='td'
                  sx={{
                    ...bodyCellSx,
                    pl: 0,
                    fontWeight: 500,
                  }}
                >
                  {t(`dimensions.${row.dimension as DepartmentHealthDimension}`)}
                </Box>
                <Box
                  component='td'
                  sx={{
                    ...bodyCellSx,
                    textAlign: 'center',
                    fontWeight: 700,
                    color: scoreColors?.color ?? 'text.secondary',
                  }}
                >
                  {formatScore(row.departmentScore)}
                </Box>
                <Box component='td' sx={{...bodyCellSx, textAlign: 'center', color: 'text.secondary'}}>
                  {formatScore(row.roleNorm)}
                </Box>
                <Box component='td' sx={{...bodyCellSx, textAlign: 'center', color: 'text.secondary'}}>
                  {formatScore(row.companyAverage)}
                </Box>
                <Box component='td' sx={{...bodyCellSx, pr: 0}}>
                  <DepartmentHealthBenchmarkDeltaChip delta={row.delta} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </EmpionPaper>
  );
};
